import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronRight,
    Heart,
    HelpCircle,
    LogOut,
    MapPin,
    Package,
    Pencil,
    Settings,
    ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { SubmitButton } from "@/components/form/SubmitButton";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/AuthContext";
import { isAdminRole, roleLabel } from "@/features/auth/roles";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

const MENU: Array<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    to?: string;
}> = [
    { label: "Saved addresses", icon: MapPin },
    { label: "Wishlist", icon: Heart },
    { label: "Your orders", icon: Package, to: ROUTES.orders },
    { label: "Preferences", icon: Settings },
    { label: "Help & support", icon: HelpCircle },
];

function initials(name: string): string {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("") || "T";
}

export default function Profile() {
    const { user, logout, updateProfile } = useAuth();
    const navigate = useNavigate();
    const [editOpen, setEditOpen] = useState(false);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!user) return null;

    const onLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate(ROUTES.welcome);
    };

    const openEdit = () => {
        setName(user.full_name);
        setPhone(user.phone);
        setError(null);
        setEditOpen(true);
    };

    const onSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await updateProfile({ full_name: name.trim(), phone: phone.trim() });
            toast.success("Profile updated");
            setEditOpen(false);
        } catch (err) {
            setError(formatApiError(err, "Couldn't update profile"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="profile-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <h1 className="tsgo-h1">Profile</h1>
                <p className="tsgo-lead mt-2">
                    Manage your account, addresses & preferences.
                </p>
            </section>

            <section className="tsgo-container mt-8 grid gap-6 lg:grid-cols-[340px_1fr]">
                <aside className="rounded-2xl border border-border/70 bg-card p-6 text-center shadow-soft">
                    <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-brand text-2xl font-extrabold text-white shadow-brand">
                        {initials(user.full_name)}
                    </div>
                    <h2 className="mt-4 text-lg font-semibold" data-testid="profile-name">
                        {user.full_name}
                    </h2>
                    <p className="truncate text-sm text-muted-foreground" data-testid="profile-email">
                        {user.email}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="profile-phone">
                        {user.phone}
                    </p>
                    {isAdminRole(user.role) ? (
                        <span
                            data-testid="profile-role-badge"
                            className="mt-3 inline-flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
                        >
                            <ShieldCheck className="h-3 w-3" />
                            {roleLabel(user.role)}
                        </span>
                    ) : (
                        <span
                            data-testid="profile-role-badge"
                            className="mt-3 inline-flex items-center rounded-full bg-brand/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-brand"
                        >
                            Customer
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={openEdit}
                        data-testid="profile-edit-btn"
                        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-brand/30 bg-brand/5 px-4 py-2 text-sm font-semibold text-brand transition hover:bg-brand hover:text-white"
                    >
                        <Pencil className="h-4 w-4" /> Edit profile
                    </button>
                </aside>

                <div className="space-y-4">
                    {isAdminRole(user.role) ? (
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.adminDashboard)}
                            data-testid="profile-admin-panel"
                            className="flex w-full items-center gap-4 rounded-2xl border border-ink/10 bg-ink px-5 py-4 text-left text-white shadow-brand transition hover:bg-ink/90"
                        >
                            <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10">
                                <ShieldCheck className="h-5 w-5 text-brand" />
                            </span>
                            <span className="flex-1">
                                <span className="block text-sm font-semibold">Open admin panel</span>
                                <span className="block text-xs text-white/70">
                                    Signed in as {roleLabel(user.role)} · full console access
                                </span>
                            </span>
                            <ChevronRight className="h-4 w-4 text-white/70" />
                        </button>
                    ) : null}

                    <ul className="divide-y divide-border/70 overflow-hidden rounded-2xl border border-border/70 bg-card">
                        {MENU.map(({ label, icon: Icon, to }) => (
                            <li key={label}>
                                <button
                                    type="button"
                                    onClick={() =>
                                        to
                                            ? navigate(to)
                                            : toast(`${label} coming soon`, {
                                                  description: "This section will be built in a later step.",
                                              })
                                    }
                                    data-testid={`profile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-muted/40"
                                >
                                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted/50 text-foreground/70">
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <span className="flex-1 text-sm font-medium">{label}</span>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                </button>
                            </li>
                        ))}
                        <li>
                            <button
                                type="button"
                                onClick={onLogout}
                                data-testid="profile-menu-logout"
                                className={cn(
                                    "flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-error/5",
                                )}
                            >
                                <span className="grid h-10 w-10 place-items-center rounded-xl bg-error/10 text-error">
                                    <LogOut className="h-5 w-5" />
                                </span>
                                <span className="flex-1 text-sm font-medium text-error">Log out</span>
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </button>
                        </li>
                    </ul>

                    <SubmitButton
                        type="button"
                        variant="outline"
                        onClick={onLogout}
                        data-testid="profile-logout-cta"
                    >
                        Sign out of TSGo
                    </SubmitButton>
                </div>
            </section>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent data-testid="profile-edit-dialog" className="sm:max-w-md">
                    <form onSubmit={onSave}>
                        <DialogHeader>
                            <DialogTitle>Edit profile</DialogTitle>
                            <DialogDescription>
                                Update your name and mobile number.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="edit-name">Full name</Label>
                                <Input
                                    id="edit-name"
                                    data-testid="profile-edit-name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="name"
                                    required
                                    minLength={2}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="edit-phone">Mobile number</Label>
                                <Input
                                    id="edit-phone"
                                    data-testid="profile-edit-phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    autoComplete="tel"
                                    required
                                    inputMode="tel"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Email</Label>
                                <Input value={user.email} disabled />
                                <p className="text-[11px] text-muted-foreground">
                                    Email is used to sign in and can't be changed here.
                                </p>
                            </div>
                            {error ? (
                                <p
                                    role="alert"
                                    data-testid="profile-edit-error"
                                    className="text-xs font-medium text-error"
                                >
                                    {error}
                                </p>
                            ) : null}
                        </div>
                        <DialogFooter className="mt-5 gap-2">
                            <button
                                type="button"
                                onClick={() => setEditOpen(false)}
                                data-testid="profile-edit-cancel"
                                className="inline-flex h-10 items-center justify-center rounded-full border border-border px-4 text-sm font-semibold text-foreground transition hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <SubmitButton
                                type="submit"
                                loading={saving}
                                fullWidth={false}
                                data-testid="profile-edit-save"
                            >
                                Save changes
                            </SubmitButton>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
